import { useCallback, useEffect, useRef, useState } from 'react';

import { MessagesType } from '@telialt/common-cms/src/model';
import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import {
    FULL_PRICE_FIELD_NAME,
    MONTHLY_PRICE_FIELD_NAME,
    SEARCH_FILTERABLE_FIELDS,
    SEARCH_PAGE_QUERY_PARAM,
} from '@telialt/core-search/src/constants';
import { SearchFacet, SearchFilters, SearchFilterValue } from '@telialt/core-search/src/model';
import { formatFilterToQueryParamValue } from '@telialt/core-search/src/utils/filtersUtils';
import { isFilterTerms } from '@telialt/core-search/src/utils/typeGuards';

import { CategoryData, SeoFilters } from '../models';
import { readCategoryPathAndFilter } from './readCategoryPathFilterValue';
import { readCategoryFilters } from './readCategorySearchParams';
import { useCategoryLocation } from './useCategoryLocation';

const readFilters = (
    pathname: string,
    searchParams: URLSearchParams,
    categoryData: CategoryData,
    t: MessagesType,
    hasMessage: (key: string) => boolean,
) => {
    const { categoryPath, pathFilter } = readCategoryPathAndFilter(pathname, categoryData);
    const searchParamsFilters = readCategoryFilters(searchParams, t, hasMessage);

    const categoryFilter: SearchFilters = { categoryPath: [categoryPath] };
    const filters = {
        ...categoryFilter,
        ...searchParamsFilters,
        ...pathFilter,
    };

    return filters;
};

const findPathFilterValue = (seoFilters: SeoFilters, fieldName: string, fieldValue: string) => {
    return seoFilters?.[fieldName]?.[fieldValue];
};

const getFilterQueryParamName = (t: MessagesType, hasMessage: (key: string) => boolean, fieldName: string) => {
    const messageKey = `eshop.category.facets.filter_url_label.${fieldName}`;

    return hasMessage(messageKey) ? t[messageKey] : fieldName;
};

const generateFiltersUrl = (
    categoryData: CategoryData,
    t: MessagesType,
    hasMessage: (key: string) => boolean,
    facets: SearchFacet[],
    fieldName: string,
    filter?: SearchFilterValue,
) => {
    const url = new URL(window.location.href);
    const seoFilters = categoryData?.filters;

    const { categoryPath, pathFilter } = readCategoryPathAndFilter(url.pathname, categoryData);

    const newPathFilterValue = findPathFilterValue(seoFilters, fieldName, filter?.[0]);

    const queryParamName = getFilterQueryParamName(t, hasMessage, fieldName);
    const queryParamValue = formatFilterToQueryParamValue(facets, fieldName, filter);
    const isFilterSet = !!filter && !!queryParamValue;
    const shouldUsePathFilter =
        isFilterSet &&
        isFilterTerms(filter) &&
        filter.length === 1 &&
        !!newPathFilterValue &&
        !Object.keys(pathFilter || {}).length;

    if (!isFilterSet) {
        // This is filter cleanup -> first cleanup it from search params. Will be noop if it is not set already
        url.searchParams.delete(queryParamName);
        if (pathFilter?.[fieldName]) {
            // Filter cleanup and this filter is set as path filter value -> need to update pathname
            url.pathname = categoryPath;
        }
    } else if (shouldUsePathFilter) {
        url.pathname = `${categoryPath}/${newPathFilterValue}`;
        url.searchParams.delete(queryParamName);
    } else {
        if (pathFilter?.[fieldName]) {
            // If there's already a path filter for this field type, convert it to query param
            // and add the new value as query param too
            url.pathname = categoryPath;
        }

        url.searchParams.set(queryParamName, queryParamValue);
    }

    // Cleanup price fields, if one price filter is selected, having another one does not make sense
    const fullPriceParamName = getFilterQueryParamName(t, hasMessage, FULL_PRICE_FIELD_NAME);
    const monthlyPriceParamName = getFilterQueryParamName(t, hasMessage, MONTHLY_PRICE_FIELD_NAME);

    if (fieldName === MONTHLY_PRICE_FIELD_NAME) {
        url.searchParams.delete(fullPriceParamName);
    } else if (fieldName === FULL_PRICE_FIELD_NAME) {
        url.searchParams.delete(monthlyPriceParamName);
    }

    // Clear page parameter - reset to first page after applying filter
    url.searchParams.delete(SEARCH_PAGE_QUERY_PARAM);

    return { updatedUrl: url.toString(), isFilterSet };
};

const generateUrlWithoutFilters = (
    categoryData: CategoryData,
    t: MessagesType,
    hasMessage: (key: string) => boolean,
) => {
    const url = new URL(window.location.href);
    const { categoryPath } = readCategoryPathAndFilter(url.pathname, categoryData);

    url.pathname = categoryPath;

    Object.keys(SEARCH_FILTERABLE_FIELDS).forEach((fieldName) => {
        const queryParamName = getFilterQueryParamName(t, hasMessage, fieldName);
        url.searchParams.delete(queryParamName);
    });

    return url.toString();
};

export const useCategoryFilterValues = (categoryData: CategoryData) => {
    const { t, hasMessage } = useTranslations();
    const translationsRef = useRef(t);

    const { pathname, searchParams } = useCategoryLocation();

    const { categoryPath } = readCategoryPathAndFilter(pathname, categoryData);

    const [filters, setFilters] = useState<SearchFilters>(() =>
        readFilters(pathname, searchParams, categoryData, t, hasMessage),
    );

    useEffect(() => {
        setFilters(readFilters(pathname, searchParams, categoryData, translationsRef.current, hasMessage));
    }, [pathname, searchParams, categoryData, hasMessage]);

    const setFilterValue = useCallback(
        (facets: SearchFacet[], fieldName: string, filter?: SearchFilterValue) => {
            const { updatedUrl, isFilterSet } = generateFiltersUrl(
                categoryData,
                translationsRef.current,
                hasMessage,
                facets,
                fieldName,
                filter,
            );

            window.history.pushState(window.history.state, null, updatedUrl);
            window.dispatchEvent(new CustomEvent('popstate'));

            setFilters((currentFilters) => {
                const clone = { ...currentFilters };
                if (isFilterSet) {
                    clone[fieldName] = filter;
                } else {
                    delete clone[fieldName];
                }

                return clone;
            });
        },
        [categoryData, hasMessage],
    );

    const removeAllFilters = useCallback(() => {
        const urlWithoutFilters = generateUrlWithoutFilters(categoryData, translationsRef.current, hasMessage);

        window.history.pushState(window.history.state, null, urlWithoutFilters);
        window.dispatchEvent(new CustomEvent('popstate'));
        setFilters({});
    }, [categoryData, hasMessage]);

    return { filters, setFilterValue, removeAllFilters, categoryPath };
};
